import { useSeo } from "../lib/seo";
import { useState } from "react";
import { Wordmark } from "../components/Wordmark";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { trackConversion } from "../lib/ads";
import { motion } from "framer-motion";
import { Flame, Mail, Lock, User, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const HERO = "https://static.prod-images.emergentagent.com/jobs/b23bf366-46b0-4cb6-93b8-62d309592fdf/images/851bfc69bb5d0253713efb928015954caef356195f8e07dc456f39b7f0915b63.jpeg";

export default function Auth() {
  useSeo({ title: "Sign in or create your free account", description: "Sign in to First Step or create a free account to practice firefighter oral boards, study exam questions and research departments.", path: "/auth", noindex: true });
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [referralCode, setReferralCode] = useState(() => (params.get("ref") || sessionStorage.getItem("fireprep-ref") || "").toUpperCase());
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setResetSent(true);
        return;
      }
      if (mode === "register") {
        if (password.length < 6) { toast.error("Password must be at least 6 characters."); setLoading(false); return; }
        if (!agreed) { toast.error("Please tick the box to accept the Terms of Use & Disclaimer."); setLoading(false); return; }
        const created = await register(name, email, password, referralCode.trim());
        sessionStorage.removeItem("fireprep-ref");
        if (referralCode.trim() && created?.referral !== "attached") toast.info(created?.referral === "self" ? "You can't use your own referral code." : "That referral code wasn't valid, so no referral was attached.");
        trackConversion("signup"); // fires only after the account was actually created
        toast.success("Welcome aboard! Your account is ready.");
      } else {
        await login(email, password);
      }
      const nxt = params.get("next"); navigate(nxt && nxt.startsWith("/") && !nxt.startsWith("//") ? nxt : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message, { duration: [423, 429].includes(err.response?.status) ? 9000 : 4000 });
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left visual */}
      <div className="hidden lg:block relative bg-slate-950">
        <img src={HERO} alt="Firefighter training" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/30" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <Flame className="w-5 h-5 text-white" />
            </div>
            <Wordmark onDark />
          </Link>
          <div>
            <h2 className="font-heading text-4xl font-bold text-white uppercase leading-tight">
              The badge is earned<br />before the test.
            </h2>
            <p className="mt-4 text-slate-300 max-w-md">Practice questions, mock oral boards, and honest feedback — all in one place.</p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center"><Flame className="w-5 h-5 text-white" /></div>
            <Wordmark />
          </Link>

          <h1 className="font-heading text-3xl font-bold text-slate-900">
            {mode === "register" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Welcome back"}
          </h1>
          <p className="mt-2 text-slate-500">
            {mode === "register" ? "Start training in under a minute." : mode === "forgot" ? "Enter the email on your account and we'll send you a link to choose a new password." : "Log in to continue your prep."}
          </p>

          {mode === "forgot" ? (
            resetSent ? (
              <div className="mt-8 rounded-2xl border border-green-200 bg-green-50 p-5 text-sm text-slate-700" data-testid="forgot-password-sent">
                <p className="font-semibold text-slate-900">Check your inbox.</p>
                <p className="mt-1">If an account exists for <b>{email}</b>, we've sent a link to reset the password. It works once and expires in 60 minutes — check your spam folder too.</p>
                <button type="button" onClick={() => { setMode("login"); setResetSent(false); }} className="mt-4 text-primary font-semibold hover:underline" data-testid="forgot-password-back">Back to log in</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4 mt-8" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className="pl-10 h-12" data-testid="forgot-password-email-input" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 bg-primary hover:bg-primary/90 font-semibold" data-testid="forgot-password-submit">
                  {loading ? "Please wait…" : "Send reset link"}{!loading && <ArrowRight className="ml-2 w-4 h-4" />}
                </Button>
                <p className="text-[11px] text-slate-400 text-center leading-snug">Signed up with Google? Just use “Continue with Google” — there's no password to reset.</p>
                <p className="text-center text-sm text-slate-500"><button type="button" onClick={() => setMode("login")} className="text-primary font-semibold hover:underline" data-testid="forgot-password-cancel">Back to log in</button></p>
              </form>
            )
          ) : (<>

          <Button onClick={googleLogin} variant="outline" className="w-full mt-8 h-12 gap-3" data-testid="google-login-button">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
          </Button>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 uppercase tracking-widest">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Full name</Label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Alex Rivera" className="pl-10 h-12" data-testid="register-name-input" />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className="pl-10 h-12" data-testid="auth-email-input" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {mode === "login" && <button type="button" onClick={() => setMode("forgot")} className="text-xs text-primary font-semibold hover:underline" data-testid="forgot-password-link">Forgot password?</button>}
              </div>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="••••••••" className="pl-10 h-12" data-testid="auth-password-input" />
              </div>
              {mode === "register" && password.length > 0 && password.length < 6 && <p className="mt-1.5 text-xs text-red-600" data-testid="auth-password-hint">Password must be at least 6 characters.</p>}
            </div>
              {mode === "register" && (
                <div className="space-y-1.5">
                  <Label htmlFor="referral" className="text-slate-600 text-xs">Referral code <span className="text-slate-400">(optional)</span></Label>
                  <Input id="referral" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} maxLength={16} placeholder="e.g. RYD7K2" className="h-11 uppercase" data-testid="register-referral-input" />
                </div>
              )}
            <Button type="submit" disabled={loading} className="w-full h-12 bg-primary hover:bg-primary/90 font-semibold" data-testid="auth-submit-button">
              {loading ? "Please wait…" : mode === "register" ? "Create account" : "Log in"}
              {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
            </Button>
            {mode === "register" ? (
              <label className="flex items-start gap-3 text-xs text-slate-500 leading-snug cursor-pointer py-1" data-testid="auth-terms-checkbox-label">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-red-600" data-testid="auth-terms-checkbox" />
                <span>I have read and agree to the <Link to="/terms#terms" className="underline hover:text-primary">Terms of Use</Link>, <Link to="/terms#privacy" className="underline hover:text-primary">Privacy Policy</Link> and <Link to="/terms#ai" className="underline hover:text-primary">AI Disclosure</Link> — First Step Fire Prep is an independent, AI-assisted preparation tool, not medical advice, and not affiliated with any testing agency or fire department.</span>
              </label>
            ) : (
              <p className="text-[11px] text-slate-400 text-center leading-snug" data-testid="auth-terms-note">By continuing you agree to our <Link to="/terms" className="underline hover:text-primary">Terms, Privacy Policy &amp; AI Disclosure</Link>.</p>
            )}
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            {mode === "register" ? "Already have an account?" : "New to First Step?"}{" "}
            <button
              onClick={() => setMode(mode === "register" ? "login" : "register")}
              className="text-primary font-semibold hover:underline"
              data-testid="auth-toggle-mode-button"
            >
              {mode === "register" ? "Log in" : "Create one"}
            </button>
          </p>
          </>)}
        </motion.div>
      </div>
    </div>
  );
}
